import type { KeyboardEvent, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { DataSurface } from './DataSurface';
import { EmptyState, type EmptyStateProps } from './EmptyState';
import { WS_COLOR, WS_CONTROL, WS_RADIUS } from './tokens';

export interface RegistryColumn<R> {
  key: string;
  /** Sentence case. Put the unit in the header, not in every cell. */
  header: string;
  /** Pixel width or CSS width. Omit to share the remaining space. */
  width?: number | string;
  /** Numbers are right-aligned with tabular figures. */
  numeric?: boolean;
  /** Identifiers (OPP-0147) render in the mono face. */
  mono?: boolean;
  cell: (row: R) => ReactNode;
}

export type RegistryDensity = 'standard' | 'compact';

export type RegistryTableState =
  | { kind: 'ready' }
  | { kind: 'loading' }
  | { kind: 'error'; onRetry?: () => void; message?: ReactNode };

export interface RegistryTableProps<R> {
  rows: readonly R[];
  columns: readonly RegistryColumn<R>[];
  getRowId: (row: R) => string;
  /** Accessible name for the table. */
  label: string;
  selectedId?: string | null;
  onSelect?: (row: R) => void;
  density?: RegistryDensity;
  state?: RegistryTableState;
  /** True when `rows` is empty BECAUSE of the active filters (shows the no-matches state). */
  filtered?: boolean;
  /** Content for an empty registry (nothing exists yet). */
  empty?: Pick<EmptyStateProps, 'title' | 'description' | 'action'>;
  /** Action under the no-matches message, normally "Clear filters". */
  noMatchesAction?: ReactNode;
}

/**
 * THE registry table (DESIGN.md layout 2).
 *
 * It fills the data region it is given and is the ONLY thing on a registry screen that
 * scrolls; its header row is sticky. Sticky works on the `th` (not the `tr`), with
 * `border-collapse: separate` and no ancestor between the `th` and the scroll box having a
 * non-visible overflow — all three were broken at once in earlier screens (CLAUDE.md).
 *
 * One header treatment replaces the per-module variants: sentence case, 13px/600, on the
 * subtle surface. Rows are 40px (36px compact), numbers are tabular and right-aligned, and
 * identifiers are mono. A selected row uses the info pair and is announced with
 * `aria-selected`-equivalent `aria-current`; rows are keyboard-operable.
 *
 * Empty, loading, no-matches and error are states of THIS component, not separate screens.
 *
 * Deliberately not here yet: column sorting, resizing and virtualisation. They are planned
 * (docs/ui-kit-plan.md) and each needs an owner ruling on behaviour first; APR's tables
 * sort nothing and the ported screens must keep matching them until a pattern is approved.
 */
export function RegistryTable<R>({
  rows,
  columns,
  getRowId,
  label,
  selectedId,
  onSelect,
  density = 'standard',
  state = { kind: 'ready' },
  filtered = false,
  empty,
  noMatchesAction,
}: RegistryTableProps<R>) {
  const rowHeight = density === 'compact' ? WS_CONTROL.rowHeightCompact : WS_CONTROL.rowHeight;
  const colCount = columns.length;

  const onKey = (event: KeyboardEvent<HTMLTableRowElement>, row: R) => {
    if (!onSelect) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(row);
    }
  };

  let body: ReactNode;
  if (state.kind === 'loading') {
    body = Array.from({ length: 8 }, (_, i) => (
      <tr key={`sk-${i}`} aria-hidden>
        {columns.map((c) => (
          <td key={c.key}>
            <Skeleton
              variant="rounded"
              height={12}
              width={c.numeric ? '50%' : `${60 + ((i * 7) % 35)}%`}
            />
          </td>
        ))}
      </tr>
    ));
  } else if (state.kind === 'ready' && rows.length > 0) {
    body = rows.map((row) => {
      const id = getRowId(row);
      const selected = id === selectedId;
      return (
        <tr
          key={id}
          data-selected={selected || undefined}
          aria-current={selected ? 'true' : undefined}
          tabIndex={onSelect ? 0 : undefined}
          onClick={onSelect ? () => onSelect(row) : undefined}
          onKeyDown={onSelect ? (e) => onKey(e, row) : undefined}
        >
          {columns.map((c) => (
            <td key={c.key} data-numeric={c.numeric || undefined} data-mono={c.mono || undefined}>
              {c.cell(row)}
            </td>
          ))}
        </tr>
      );
    });
  }

  let message: ReactNode = null;
  if (state.kind === 'error') {
    message = (
      <EmptyState
        kind="error"
        description={state.message ?? 'The server did not respond. Your changes are safe.'}
        action={
          state.onRetry ? (
            <Box component="button" onClick={state.onRetry} sx={retryButton}>
              Try again
            </Box>
          ) : undefined
        }
      />
    );
  } else if (state.kind === 'ready' && rows.length === 0) {
    message = filtered ? (
      <EmptyState
        kind="no-matches"
        description="Try a different value or clear the search."
        action={noMatchesAction}
      />
    ) : (
      <EmptyState kind="empty" {...empty} />
    );
  }

  return (
    <DataSurface rowHeight={rowHeight}>
      <table aria-label={label} aria-busy={state.kind === 'loading' || undefined}>
        <colgroup>
          {columns.map((c) => (
            <col key={c.key} style={c.width ? { width: c.width } : undefined} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} scope="col" data-numeric={c.numeric || undefined}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{body}</tbody>
      </table>
      {message ? <Box sx={{ gridColumn: `1 / ${colCount + 1}` }}>{message}</Box> : null}
    </DataSurface>
  );
}

const retryButton = {
  height: WS_CONTROL.height,
  px: 5,
  border: 0,
  borderRadius: `${WS_RADIUS.button}px`,
  backgroundColor: WS_COLOR.tonal,
  color: WS_COLOR.onTonal,
  font: 'inherit',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
} as const;
